import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { getAlert } from "../../services/appAlerts";

const DefaultDocumentsModal = ({
  show,
  onHide,
  defaultDocuments,
  onSave,
  reqDocuments = [],
  dataTypeMappings = [],
  ddrmsDocs = [],
  performerType = "",
  renderPurchaseOrder,
  renderGenerate,
  renderUploadReference
}) => {
  const [activeTab, setActiveTab] = useState("documents"); // "documents" | "action" | "add"
  const [actionFooterElement, setActionFooterElement] = useState(null);
  const [tempDocuments, setTempDocuments] = useState([]);
  const [selectedDocsToLoad, setSelectedDocsToLoad] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [actionTab, setActionTab] = useState(null);
  const actionTabOpenRef = useRef(false);
  const [bulkItemStatuses, setBulkItemStatuses] = useState({});
  const [stagedReferenceDocs, setStagedReferenceDocs] = useState({});
  const [stagedGeneratedDocs, setStagedGeneratedDocs] = useState({});
  const [bulkProcess, setBulkProcess] = useState({
    active: false,
    items: [],
    currentIndex: 0,
    completed: 0,
    total: 0,
    status: "idle"
  });

  // Form State
  const [newDocName, setNewDocName] = useState("");
  const [newDocAction, setNewDocAction] = useState("upload"); // "upload" | "generate"
  const [newDdrmsDocId, setNewDdrmsDocId] = useState("");
  const [newMappingId, setNewMappingId] = useState("");
  const [newReqDocId, setNewReqDocId] = useState("");

  const getDocumentAction = (doc) => {
    if (
      doc?.purchaseOrderType ||
      String(doc?.name || "").trim().toLowerCase().startsWith("purchase order")
    ) {
      return "generate";
    }
    const configuredAction = String(doc?.action ?? doc?.Action ?? "").trim().toLowerCase();
    if (configuredAction === "generate" || configuredAction === "upload") {
      return configuredAction;
    }
    return doc?.ddrmsDocId && doc?.mappingId ? "generate" : "upload";
  };
  const isGenerateDocument = (doc) => getDocumentAction(doc) === "generate";
  const isGenerateReady = (doc) =>
    isGenerateDocument(doc) && Boolean(doc?.ddrmsDocId && doc?.mappingId);
  const isPurchaseOrderDocument = (doc) =>
    Boolean(doc?.purchaseOrderType) ||
    String(doc?.name || "").trim().toLowerCase().startsWith("purchase order");
  const isBulkEligible = (doc) =>
    isPurchaseOrderDocument(doc)
      ? true
      : isGenerateDocument(doc)
        ? isGenerateReady(doc)
        : Boolean(doc?.reqDocId);
  const getBulkItemKey = (type, doc, idx) =>
    `${type}:${idx !== undefined ? idx : (doc?.ddrmsDocId || doc?.reqDocId || doc?.name)}`;
  const getReferenceKey = (doc) => String(doc?.reqDocId || doc?.name || "");
  const getGeneratedKey = (doc) => String(doc?.ddrmsDocId || doc?.name || "");
  const getStagedPurchaseOrder = (doc) =>
    isPurchaseOrderDocument(doc) ? stagedGeneratedDocs[getGeneratedKey(doc)] || null : null;
  const handleRemoveStagedPurchaseOrder = (doc, idx) => {
    if (bulkProcess.active) return;
    const generatedKey = getGeneratedKey(doc);
    setStagedGeneratedDocs((current) => {
      const updated = { ...current };
      delete updated[generatedKey];
      return updated;
    });
    setBulkItemStatuses((current) => {
      const updated = { ...current };
      delete updated[getBulkItemKey("purchaseOrder", doc, idx)];
      return updated;
    });
    setSelectedDocsToLoad((current) => current.filter((selectedIdx) => selectedIdx !== idx));
  };
  const getGenerateRenderOptions = (doc, autoProcess) => {
    const generatedKey = getGeneratedKey(doc);
    return {
      autoProcess,
      footerContainer: autoProcess ? null : actionFooterElement,
      stagedGenerated: stagedGeneratedDocs[generatedKey] || null,
      onStageGenerated: (generatedData) => {
        setStagedGeneratedDocs((current) => ({ ...current, [generatedKey]: generatedData }));
        if (isPurchaseOrderDocument(doc)) {
          const docIndex = tempDocuments.indexOf(doc);
          if (docIndex >= 0) {
            setSelectedDocsToLoad((current) => [...new Set([...current, docIndex])]);
          }
        }
      }
    };
  };
  const getUploadRenderOptions = (doc, autoProcess) => {
    const referenceKey = getReferenceKey(doc);
    return {
      autoProcess,
      stagedReference: stagedReferenceDocs[referenceKey] || null,
      onStageReference: (referenceData) => {
        setStagedReferenceDocs((current) => ({ ...current, [referenceKey]: referenceData }));
      }
    };
  };

  useEffect(() => {
    if (!show || actionTabOpenRef.current) return;

    if (defaultDocuments && defaultDocuments.length > 0) {
      setTempDocuments(JSON.parse(JSON.stringify(defaultDocuments)));
      setSelectedDocsToLoad(
        defaultDocuments
          .map((doc, i) => ({ doc, i }))
          .filter(({ doc }) => isBulkEligible(doc))
          .map(({ i }) => i)
      );
    } else {
      setTempDocuments([]);
      setSelectedDocsToLoad([]);
    }
    setEditIndex(null);
    setActionTab(null);
    setBulkItemStatuses({});
    setStagedReferenceDocs({});
    setStagedGeneratedDocs({});
    actionTabOpenRef.current = false;
    setBulkProcess({ active: false, items: [], currentIndex: 0, completed: 0, total: 0, status: "idle" });
    setActiveTab("documents");
  }, [defaultDocuments, show]);

  const handleToggleTempDoc = (idx) => {
    if (bulkProcess.active) return;
    if (!isBulkEligible(tempDocuments[idx])) return;

    if (selectedDocsToLoad.includes(idx)) {
      setSelectedDocsToLoad(selectedDocsToLoad.filter((i) => i !== idx));
    } else {
      setSelectedDocsToLoad([...selectedDocsToLoad, idx]);
    }
  };

  const handleDeleteDoc = (idxToDelete) => {
    if (bulkProcess.active) return;

    const updated = tempDocuments.filter((_, i) => i !== idxToDelete);
    setTempDocuments(updated);

    // Update selected indices list
    const newSelected = selectedDocsToLoad
      .filter((i) => i !== idxToDelete)
      .map((i) => (i > idxToDelete ? i - 1 : i));
    setSelectedDocsToLoad(newSelected);

    if (editIndex === idxToDelete) {
      setEditIndex(null);
      setNewDocName("");
      setNewDdrmsDocId("");
      setNewMappingId("");
      setNewReqDocId("");
    } else if (editIndex > idxToDelete) {
      setEditIndex(editIndex - 1);
    }
  };

  const handleEditDocClick = (idx) => {
    if (bulkProcess.active) return;

    const doc = tempDocuments[idx];
    setEditIndex(idx);
    setNewDocName(doc.name || "");
    setNewDocAction(getDocumentAction(doc));
    setNewDdrmsDocId(doc.ddrmsDocId !== null && doc.ddrmsDocId !== undefined ? String(doc.ddrmsDocId) : "");
    setNewMappingId(doc.mappingId !== null && doc.mappingId !== undefined ? String(doc.mappingId) : "");
    setNewReqDocId(doc.reqDocId !== null && doc.reqDocId !== undefined ? String(doc.reqDocId) : "");
    setActiveTab("add");
  };

  const handleSaveDoc = (e) => {
    e.preventDefault();
    if (!newDocName.trim()) return;

    const existingDoc = editIndex !== null ? tempDocuments[editIndex] : null;
    const targetDoc = isPurchaseOrderDocument(existingDoc)
      ? { ...existingDoc, name: newDocName.trim() }
      : {
        name: newDocName.trim(),
        action: newDocAction,
        ddrmsDocId: newDocAction === "generate" ? (parseInt(newDdrmsDocId) || null) : null,
        mappingId: newDocAction === "generate" ? (parseInt(newMappingId) || null) : null,
        reqDocId: newDocAction === "upload" ? (parseInt(newReqDocId) || null) : null,
      };

    const updated = [...tempDocuments];
    if (editIndex !== null) {
      updated[editIndex] = targetDoc;
      setSelectedDocsToLoad((current) => {
        const withoutEditedDoc = current.filter((idx) => idx !== editIndex);
        return isBulkEligible(targetDoc)
          ? [...new Set([...withoutEditedDoc, editIndex])]
          : withoutEditedDoc;
      });
      setEditIndex(null);
    } else {
      updated.push(targetDoc);
      if (isBulkEligible(targetDoc)) {
        setSelectedDocsToLoad([...selectedDocsToLoad, updated.length - 1]);
      }
    }

    setTempDocuments(updated);

    // Reset Form Fields
    setNewDocName("");
    setNewDdrmsDocId("");
    setNewMappingId("");
    setNewReqDocId("");

    setActiveTab("documents");
  };

  const handleCancelDocumentEdit = () => {
    setEditIndex(null);
    setNewDocName("");
    setNewDdrmsDocId("");
    setNewMappingId("");
    setNewReqDocId("");
    setActiveTab("documents");
  };

  const handleSave = () => {
    if (bulkProcess.active) return;
    onSave(tempDocuments);
  };

  // Drop any staged/in-progress state before closing so the next open starts
  // clean instead of resurfacing the previous session's reference/generated docs.
  const handleClose = () => {
    if (bulkProcess.active) return;
    actionTabOpenRef.current = false;
    setActionTab(null);
    setBulkItemStatuses({});
    setStagedReferenceDocs({});
    setStagedGeneratedDocs({});
    setBulkProcess({ active: false, items: [], currentIndex: 0, completed: 0, total: 0, status: "idle" });
    setActiveTab("documents");
    onHide();
  };

  const handleOpenActionTab = (type, doc, bulk = false) => {
    if (bulkProcess.active && !bulk) return;

    actionTabOpenRef.current = true;
    setActionTab({ type, doc, bulk });
    setActiveTab(bulk ? "documents" : "action");
  };

  const handleCloseActionTab = (completed = false) => {
    if (actionTab?.bulk && bulkProcess.active) {
      if (!completed) {
        actionTabOpenRef.current = false;
        setActionTab(null);
        setBulkProcess((current) => ({ ...current, active: false, status: "cancelled" }));
        setActiveTab("documents");
        return;
      }

      const completedCount = bulkProcess.currentIndex + 1;
      const nextIndex = bulkProcess.currentIndex + 1;
      const completedItem = bulkProcess.items[bulkProcess.currentIndex];
      if (completedItem) {
        const completedPurchaseOrder = completedItem.type === "purchaseOrder"
          ? stagedGeneratedDocs[getGeneratedKey(completedItem.doc)]
          : null;
        setBulkItemStatuses((current) => ({
          ...current,
          [getBulkItemKey(completedItem.type, completedItem.doc, completedItem.idx)]:
            completedItem.type === "upload" || completedPurchaseOrder?.kind === "uploaded"
              ? "uploaded"
              : "generated"
        }));
      }
      if (nextIndex < bulkProcess.items.length) {
        const nextItem = bulkProcess.items[nextIndex];
        setBulkProcess((current) => ({
          ...current,
          currentIndex: nextIndex,
          completed: completedCount
        }));
        setActionTab({ ...nextItem, bulk: true });
        setActiveTab("documents");
        return;
      }

      actionTabOpenRef.current = false;
      setActionTab(null);
      setBulkProcess((current) => ({
        ...current,
        active: false,
        completed: completedCount,
        status: "complete"
      }));
      // Everything selected has been pushed to the server, so drop the in-memory
      // staged copies. Leaving them here makes a later action tab reopen with the
      // previous run's reference/generated file still attached.
      setStagedReferenceDocs({});
      setStagedGeneratedDocs({});
      getAlert("", "All selected documents uploaded successfully", "success");
      setActiveTab("documents");
      return;
    }

    actionTabOpenRef.current = false;
    setActionTab(null);
    setActiveTab("documents");
  };

  const handleStartBulkUpload = () => {
    const selectedItems = tempDocuments
      .map((doc, idx) => ({
        doc,
        idx,
        type: isPurchaseOrderDocument(doc) ? "purchaseOrder" : getDocumentAction(doc)
      }))
      .filter((item) => selectedDocsToLoad.includes(item.idx) && isBulkEligible(item.doc))
      .sort((a, b) => {
        const processOrder = { generate: 0, purchaseOrder: 1, upload: 2 };
        return a.type === b.type
          ? a.idx - b.idx
          : processOrder[a.type] - processOrder[b.type];
      });

    if (selectedItems.length === 0) return;

    const items = selectedItems.map(({ doc, type, idx }) => ({ doc, type, idx }));
    setBulkItemStatuses({});
    setBulkProcess({
      active: true,
      items,
      currentIndex: 0,
      completed: 0,
      total: items.length,
      status: "running"
    });
    handleOpenActionTab(items[0].type, items[0].doc, true);
  };

  const bulkProgressPercent = bulkProcess.total > 0
    ? Math.round((bulkProcess.completed / bulkProcess.total) * 100)
    : 0;
  const isProcessing = bulkProcess.active;

  // Segregate documents
  const generateDocs = tempDocuments
    .map((doc, idx) => ({ doc, idx }))
    .filter((item) => isGenerateDocument(item.doc));
  const eligibleGenerateDocs = generateDocs.filter((item) => isBulkEligible(item.doc));

  const uploadDocs = tempDocuments
    .map((doc, idx) => ({ doc, idx }))
    .filter((item) => !isGenerateDocument(item.doc));
  const eligibleUploadDocs = uploadDocs.filter((item) => Boolean(item.doc.reqDocId));

  // Segregated Select All Handlers
  const allGenerateSelected = eligibleGenerateDocs.length > 0 && eligibleGenerateDocs.every(item => selectedDocsToLoad.includes(item.idx));
  const handleToggleAllGenerate = () => {
    if (bulkProcess.active) return;
    const generateIndices = eligibleGenerateDocs.map(item => item.idx);
    if (allGenerateSelected) {
      setSelectedDocsToLoad(selectedDocsToLoad.filter(i => !generateIndices.includes(i)));
    } else {
      setSelectedDocsToLoad([...new Set([...selectedDocsToLoad, ...generateIndices])]);
    }
  };

  const allUploadSelected = eligibleUploadDocs.length > 0 && eligibleUploadDocs.every(item => selectedDocsToLoad.includes(item.idx));
  const handleToggleAllUpload = () => {
    if (bulkProcess.active) return;
    const uploadIndices = eligibleUploadDocs.map(item => item.idx);
    if (allUploadSelected) {
      setSelectedDocsToLoad(selectedDocsToLoad.filter(i => !uploadIndices.includes(i)));
    } else {
      setSelectedDocsToLoad([...new Set([...selectedDocsToLoad, ...uploadIndices])]);
    }
  };
  const editingPurchaseOrder =
    editIndex !== null && isPurchaseOrderDocument(tempDocuments[editIndex]);

  return (
    <Modal
      show={show}
      size="xl"
      centered
      onHide={isProcessing ? undefined : handleClose}
      dialogClassName="rt-tools-modal crd-modal"
      backdrop="static"
      keyboard={false}
    >
      <style>{`
        .modal-backdrop {
            z-index: 1080 !important;
        }
        .modal {
            z-index: 1090 !important;
        }
        .modal.fade .modal-dialog {
            transform: scale(0.95) !important;
            transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
        }
        .modal.show .modal-dialog {
            transform: scale(1) !important;
        }
        .crd-modal {
            max-width: 90% !important;
            width: 1200px !important;
            font-family: "Open Sans", sans-serif !important;
            color: #1e293b !important;
        }
        .crd-modal .modal-content {
            border-radius: 0 !important;
            overflow: hidden !important;
            background: #ffffff !important;
            border: 1px solid #cbd5e1 !important;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
            height: auto !important;
            max-height: calc(100vh - 60px) !important;
            display: flex !important;
            flex-direction: column !important;
        }
        .crd-modal .modal-body {
            display: flex !important;
            flex: 1 1 auto !important;
            min-height: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
        }
        .crd-shell {
            background: #ffffff;
            display: flex;
            flex-direction: column;
            min-height: 0;
            flex: 1;
            overflow: hidden;
            width: 100% !important;
        }
        .crd-modal .modal-header {
            position: relative !important;
            display: block !important;
            padding: 24px 28px 20px 32px !important;
            background: #ffffff !important;
            border-bottom: 1px solid #edf2f7 !important;
            overflow: hidden !important;
        }
        .crd-modal .modal-header::before {
            display: none !important;
        }
        .crd-modal .rt-header-shell {
            display: flex !important;
            flex-direction: column !important;
            gap: 12px !important;
            width: 100% !important;
        }
        .crd-modal .rt-header-top {
            display: flex !important;
            justify-content: space-between !important;
            align-items: flex-start !important;
            gap: 16px !important;
            width: 100% !important;
        }
        .crd-modal .rt-header-main {
            display: flex !important;
            align-items: center !important;
            gap: 14px !important;
            min-width: 0 !important;
            flex: 1 1 auto !important;
        }
        .crd-modal .rt-header-icon {
            width: 48px !important;
            height: 48px !important;
            min-height: 48px !important;
            border-radius: 0 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            color: #171717 !important;
            background: #ffffff !important;
            border: 1px solid #cbd5e1 !important;
            box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08) !important;
            font-size: 18px !important;
            flex-shrink: 0 !important;
        }
        .crd-modal .rt-header-copy {
            display: flex !important;
            flex-direction: column !important;
            gap: 6px !important;
            min-width: 0 !important;
        }
        .crd-modal .rt-modal-title {
            display: inline-flex !important;
            align-items: center !important;
            margin: 0 !important;
            color: #1e293b !important;
            font-size: 18px !important;
            font-weight: 500 !important;
            line-height: 1.2 !important;
        }
        .crd-modal .rt-modal-title-text {
            display: inline-flex !important;
            align-items: center !important;
            gap: 10px !important;
            flex-wrap: wrap !important;
        }
        .crd-modal .rt-modal-meta-row {
            display: flex !important;
            align-items: center !important;
            flex-wrap: wrap !important;
            gap: 8px !important;
            font-size: 12px !important;
        }
        .crd-modal .rt-meta-chip {
            display: inline-flex !important;
            align-items: center !important;
            gap: 5px !important;
            background: #ffffff !important;
            padding: 3px 8px !important;
            height: 24px !important;
            min-height: 24px !important;
            box-sizing: border-box !important;
            border-radius: 0 !important;
            font-size: 11.5px !important;
            font-weight: 500 !important;
            color: #0f172a !important;
            border: 1px solid #cbd5e1 !important;
        }
        .crd-modal .rt-action-btn {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 38px !important;
            height: 38px !important;
            padding: 0 !important;
            font-size: 14px !important;
            color: #64748b !important;
            background: #ffffff !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 0 !important;
            cursor: pointer !important;
            transition: all 0.2s ease !important;
            box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08) !important;
            outline: none !important;
        }
        .crd-modal .rt-action-btn:hover {
            color: #1e293b !important;
            border-color: #94a3b8 !important;
            background: #f8fafc !important;
            transform: translateY(-1px) !important;
        }
        .crd-modal .rt-action-btn:hover i {
            color: #ef4444 !important;
        }
        
        .crd-modal .modal-footer,
        .crd-footer {
            display: flex !important;
            align-items: center !important;
            justify-content: flex-end !important;
            gap: 12px !important;
            padding: 16px 28px !important;
            background: #f8fafc !important;
            border-top: 1px solid #edf2f7 !important;
            margin-top: auto !important;
        }
        .crd-modal .wf-modal-footer-close-btn {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 6px !important;
            height: 36px !important;
            padding: 0 16px !important;
            font-size: 13px !important;
            font-weight: 500 !important;
            color: #475569 !important;
            background: #ffffff !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 0 !important;
            box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08) !important;
            outline: none !important;
            cursor: pointer !important;
            transition: all 0.2s ease !important;
        }
        .crd-modal .wf-modal-footer-close-btn:hover {
            background: #f8fafc !important;
            color: #1e293b !important;
            border-color: #94a3b8 !important;
            transform: translateY(-1px) !important;
        }
        .crd-modal .wf-modal-footer-close-btn:hover i {
            color: #ef4444 !important;
        }
        .crd-modal .wf-modal-footer-close-btn:disabled {
            opacity: 0.6 !important;
            cursor: not-allowed !important;
            transform: none !important;
        }

        .crd-body {
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            min-height: 0;
            overflow: hidden;
            background: #ffffff;
        }
        .crd-tabs-container {
            display: flex;
            align-items: stretch;
            background: #ffffff;
            padding: 14px 24px 0;
            position: relative;
            flex: 0 0 auto;
            box-sizing: border-box;
        }
        .crd-tabs-list {
            display: flex;
            align-items: flex-end;
            gap: 4px;
            width: 100%;
            border-bottom: 2px solid #e5e5e5;
            margin: 0;
            padding: 0;
        }
        .crd-tab-item {
            position: relative;
            display: inline-flex;
            align-items: center;
            gap: 7px;
            border: none;
            border-bottom: 2px solid transparent;
            background: transparent;
            padding: 9px 16px 10px;
            font-size: 14px;
            font-weight: 500;
            color: #1e293b;
            cursor: pointer;
            border-radius: 0 !important;
            transition: color 0.2s ease, background 0.2s ease;
            outline: none;
            white-space: nowrap;
            box-sizing: border-box;
            margin-bottom: -2px;
        }
        .crd-tab-item i {
            font-size: 14px;
            color: currentColor;
            transition: color 0.2s ease;
        }
        .crd-tab-item:hover {
            color: #0f172a;
            background: #f5f5f5;
        }
        .crd-tab-item:focus,
        .crd-tab-item:focus-visible,
        .crd-tab-item:active {
            outline: none !important;
            box-shadow: none !important;
            border-color: transparent !important;
        }
        .crd-tab-item.active {
            color: #ffffff !important;
            font-weight: 500;
            background: #171717 !important;
            border-bottom: 2px solid #171717 !important;
            box-shadow: none !important;
        }
        .crd-tab-item.active i {
            color: #ffffff !important;
        }
        .crd-tab-count {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 20px;
            height: 20px;
            padding: 0 6px;
            border-radius: 0 !important;
            font-size: 12px;
            font-weight: 600;
            background: #f1f5f9;
            color: #1e293b;
            border: 1px solid #e2e8f0;
            transition: all 0.2s ease;
            line-height: 1;
        }
        .crd-tab-item.active .crd-tab-count {
            background: #ffffff !important;
            color: #171717 !important;
            border-color: #ffffff !important;
        }
        .crd-tab-close {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 18px !important;
            height: 18px !important;
            margin-left: 6px !important;
            border-radius: 0 !important;
            color: #64748b !important;
            cursor: pointer;
            transition: all 0.15s ease;
        }
        .crd-tab-item.active .crd-tab-close {
            color: #cbd5e1 !important;
        }
        .crd-tab-item.active .crd-tab-close:hover {
            background: rgba(255, 255, 255, 0.2) !important;
            color: #ffffff !important;
        }
        .crd-tab-item:not(.active) .crd-tab-close:hover {
            background: #fee2e2 !important;
            color: #dc2626 !important;
        }

        .crd-action-tab-pane {
            padding: 20px 24px;
            overflow-y: auto;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
            gap: 14px;
            width: 100% !important;
            box-sizing: border-box !important;
            background: #ffffff;
        }
        .crd-action-body-toolbar {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            margin-bottom: 2px;
            width: 100%;
        }
        .crd-action-body-toolbar .crd-btn {
            height: 32px;
            min-height: 32px;
            padding: 0 14px;
            font-size: 12.5px;
            gap: 7px;
            color: #334155;
            background: #ffffff;
            border: 1px solid #cbd5e1;
            border-radius: 0 !important;
            box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        .crd-action-body-toolbar .crd-btn:hover {
            color: #0f172a;
            border-color: #94a3b8;
            background: #f8fafc;
            transform: translateY(-1px);
        }
        .crd-action-body-toolbar .crd-btn i {
            color: #2563eb;
        }
        .crd-action-tab-pane .gc-embedded,
        .crd-action-tab-pane .uo-embedded,
        .crd-action-tab-pane .pop-container {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            display: flex !important;
            flex-direction: column !important;
            flex: 1 1 auto !important;
            margin: 0 !important;
        }
        .crd-action-tab-pane .po-embedded-generator {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            overflow: visible !important;
        }
        .crd-action-footer-host {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 10px;
            width: 100%;
        }
        .crd-action-tab-pane .gc-shell,
        .crd-action-tab-pane .uo-shell {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            display: flex !important;
            flex-direction: column !important;
            flex: 1 1 auto !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            background: #ffffff !important;
        }
        .crd-action-tab-pane .gc-body-only .gc-shell,
        .crd-action-tab-pane .uo-body-only .uo-shell {
            border: 1px solid #cbd5e1 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
        }
        .crd-action-tab-pane .gc-body,
        .crd-action-tab-pane .uo-body {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            padding: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            flex: 1 1 auto !important;
        }
        .crd-action-tab-pane .gc-panel {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            border: none !important;
            box-shadow: none !important;
            display: flex !important;
            flex-direction: column !important;
            flex: 1 1 auto !important;
        }
        .crd-action-tab-pane .dm-wrap {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
        }
        .crd-action-tab-pane .gc-selected-summary {
            width: 100% !important;
            box-sizing: border-box !important;
        }

        .tab-pane-list {
            padding: 20px 24px;
            overflow-y: auto;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
            gap: 20px;
        }
        .crd-section-panel {
            background: #ffffff;
            border: 1px solid #cbd5e1;
            border-radius: 0 !important;
            padding: 16px 20px 20px;
            box-shadow: none !important;
        }
        .crd-section-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 14px;
            padding-bottom: 12px;
            border-bottom: 1px solid #f1f5f9;
        }
        .section-heading {
            font-size: 12px;
            font-weight: 700;
            color: #334155;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0;
        }
        .section-heading i {
            color: #171717;
        }
        .crd-section-heading-reset {
            margin: 0;
        }
        .crd-select-all {
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            color: #334155;
            font-size: 12px;
            font-weight: 600;
            user-select: none;
        }
        .crd-select-all.disabled {
            cursor: not-allowed;
            opacity: 0.45;
        }

        /* Checkboxes */
        .crd-doc-check-center {
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .crd-checkbox-wrap {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            position: relative;
            width: 18px;
            height: 18px;
            flex-shrink: 0;
        }
        .crd-checkbox-custom {
            width: 18px;
            height: 18px;
            border: 2px solid #cbd5e1;
            border-radius: 0 !important;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #ffffff;
            box-sizing: border-box;
        }
        .crd-checkbox-row:hover:not(.disabled) .crd-checkbox-custom,
        .crd-checkbox-trigger:hover:not(.disabled) .crd-checkbox-custom {
            border-color: #2563eb;
        }
        .crd-checkbox-custom::after {
            content: "";
            display: none;
            width: 5px;
            height: 9px;
            border: solid white;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
            margin-bottom: 2px;
        }
        .crd-checkbox-row.selected .crd-checkbox-custom,
        .crd-checkbox-trigger.selected .crd-checkbox-custom {
            background-color: #2563eb;
            border-color: #2563eb;
        }
        .crd-checkbox-row.selected .crd-checkbox-custom::after,
        .crd-checkbox-trigger.selected .crd-checkbox-custom::after {
            display: block;
        }
        .crd-checkbox-trigger {
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
        }
        .crd-checkbox-trigger.disabled {
            cursor: not-allowed;
            opacity: 0.45;
        }

        /* Card-style list rows */
        .crd-modal .pdm-doc-list {
            display: flex;
            flex-direction: column;
            gap: 0;
            border-top: 1px solid #f1f5f9;
        }
        .crd-modal .pdm-doc-row {
            display: grid;
            align-items: center;
            border: 0;
            border-bottom: 1px solid #f1f5f9;
            border-radius: 0 !important;
            padding: 12px 10px;
            gap: 14px;
            transition: background 0.15s ease;
            position: relative;
            background: #ffffff;
        }
        .crd-modal .pdm-doc-row:last-child {
            border-bottom: none;
        }
        .crd-modal .pdm-doc-row:hover {
            background: #f8fafc;
        }
        .crd-modal .pdm-doc-row-card {
            grid-template-columns: 24px minmax(0, 1fr) auto;
            align-items: center;
        }
        .crd-modal .pdm-doc-file {
            display: flex;
            align-items: center;
            gap: 12px;
            min-width: 0;
        }
        .crd-modal .pdm-doc-file-icon {
            width: 36px;
            height: 36px;
            border-radius: 0 !important;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            color: #475569;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 36px;
            font-size: 15px;
        }
        .crd-modal .pdm-doc-file-icon .fa-file-pdf-o {
            color: #dc2626;
        }
        .crd-modal .pdm-doc-file-icon .fa-list-alt {
            color: #2563eb;
        }
        .crd-modal .pdm-doc-file-main {
            min-width: 0;
            text-align: left;
        }
        .crd-modal .pdm-doc-title-row {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
        }
        .crd-modal .pdm-doc-required-label {
            font-size: 13.5px;
            font-weight: 600;
            color: #0f172a;
            line-height: 1.3;
        }
        .crd-modal .pdm-doc-meta {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-top: 4px;
            flex-wrap: wrap;
            font-size: 12px;
            color: #64748b;
        }
        .crd-not-linked {
            color: #94a3b8;
            font-style: italic;
        }

        /* Action buttons */
        .crd-doc-actions {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;
        }
        .crd-action-btn {
            min-height: 32px;
            height: 32px;
            padding: 0 14px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            font-size: 12.5px;
            font-weight: 500;
            border-radius: 0 !important;
            border: 1px solid #cbd5e1;
            background: #ffffff;
            color: #1e293b;
            cursor: pointer;
            transition: all 0.15s ease;
            outline: none;
            white-space: nowrap;
        }
        .crd-action-btn:hover:not(:disabled) {
            border-color: #94a3b8;
            background: #f8fafc;
            color: #0f172a;
            transform: translateY(-1px);
        }
        .crd-action-btn .fa-magic {
            color: #7c3aed;
        }
        .crd-action-btn .fa-cloud-upload {
            color: #2563eb;
        }
        .crd-action-btn .fa-paperclip {
            color: #2563eb;
        }
        .crd-icon-action-btn {
            width: 32px;
            height: 32px;
            min-height: 32px;
            padding: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 0 !important;
            border: 1px solid #cbd5e1;
            background: #ffffff;
            cursor: pointer;
            transition: all 0.15s ease;
            outline: none;
        }
        .crd-btn-ghost-indigo,
        .crd-btn-ghost-indigo.crd-icon-action-btn,
        .crd-icon-action-btn .fa-pencil {
            color: #2563eb !important;
        }
        .crd-btn-ghost-indigo:hover:not(:disabled) {
            background: #eff6ff !important;
            border-color: #2563eb !important;
            color: #1d4ed8 !important;
            transform: translateY(-1px);
        }
        .crd-btn-ghost-danger,
        .crd-btn-ghost-danger.crd-icon-action-btn,
        .crd-icon-action-btn .fa-trash {
            color: #ef4444 !important;
        }
        .crd-btn-ghost-danger:hover:not(:disabled) {
            background: #fef2f2 !important;
            border-color: #ef4444 !important;
            color: #dc2626 !important;
            transform: translateY(-1px);
        }

        .crd-row-status {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            padding: 3px 8px;
            border: 1px solid #bbf7d0;
            border-radius: 0 !important;
            background: #f0fdf4;
            color: #15803d;
            font-size: 11px;
            font-weight: 600;
            white-space: nowrap;
        }
        .crd-row-status.staged {
            border-color: #cbd5e1;
            background: #f8fafc;
            color: #1e293b;
        }

        .crd-empty-config {
            padding: 32px 0;
            color: #94a3b8;
            text-align: center;
        }
        .crd-empty-config-icon {
            display: block;
            margin-bottom: 8px;
            color: #cbd5e1;
            font-size: 28px;
        }
        .crd-empty-config-text {
            font-size: 13px;
            font-style: italic;
        }

        /* Bulk Bar & Progress */
        .crd-bulk-progress {
            margin: 0 24px;
            padding: 12px 14px;
            border: 1px solid #cbd5e1;
            border-radius: 0 !important;
            background: #f8fafc;
        }
        .crd-bulk-progress-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 8px;
            color: #334155;
            font-size: 12px;
            font-weight: 600;
        }
        .crd-bulk-progress-native {
            display: block;
            width: 100%;
            height: 6px;
            overflow: hidden;
            appearance: none;
            border: 0;
            border-radius: 0 !important;
            background: #e2e8f0;
        }
        .crd-bulk-progress-native::-webkit-progress-bar {
            border-radius: 0;
            background: #e2e8f0;
        }
        .crd-bulk-progress-native::-webkit-progress-value {
            border-radius: 0;
            background: #171717;
            transition: width 0.3s ease;
        }
        .crd-bulk-progress-native::-moz-progress-bar {
            border-radius: 0;
            background: #171717;
            transition: width 0.3s ease;
        }
        .crd-bulk-progress.complete {
            border-color: #bbf7d0;
            background: #f0fdf4;
        }
        .crd-bulk-progress.complete .crd-bulk-progress-native::-webkit-progress-value {
            background: #16a34a;
        }
        .crd-bulk-progress.cancelled {
            border-color: #fed7aa;
            background: #fff7ed;
        }

        .crd-background-processor {
            position: fixed !important;
            top: 0 !important;
            left: -10000px !important;
            width: 1200px !important;
            max-width: none !important;
            pointer-events: none !important;
        }

        .crd-btn {
            min-height: 36px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            padding: 7px 16px;
            border-radius: 0 !important;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.15s ease;
            white-space: nowrap;
            border: 1px solid #cbd5e1;
            background: #ffffff;
            color: #1e293b;
            box-shadow: 0 2px 5px rgba(15, 23, 42, 0.08);
            outline: none;
        }
        .crd-btn:hover:not(:disabled) {
            background: #f8fafc;
            color: #0f172a;
            border-color: #94a3b8;
            transform: translateY(-1px);
        }
        .crd-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            box-shadow: none;
            transform: none;
        }
        .crd-btn.primary {
            background: #2563eb;
            border-color: #2563eb;
            color: #ffffff;
            box-shadow: 0 2px 5px rgba(37, 99, 235, 0.2);
        }
        .crd-btn.primary:hover:not(:disabled) {
            background: #1d4ed8;
            border-color: #1d4ed8;
            color: #ffffff;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
        }

        /* Add Document Tab & Form */
        .crd-add-tab {
            padding: 20px 24px;
            overflow-y: auto;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
            background: #ffffff;
        }
        .crd-add-panel {
            background: #ffffff;
            border: 1px solid #cbd5e1;
            border-radius: 0 !important;
            padding: 20px 24px 24px;
            width: 100%;
            margin: 0;
            box-shadow: none !important;
        }
        .crd-add-intro {
            margin: 0 0 20px;
            color: #64748b;
            font-size: 13px;
            line-height: 1.5;
        }
        .crd-add-form {
            display: flex;
            flex-direction: column;
            gap: 18px;
        }
        .crd-add-main-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
        }
        .crd-add-main-grid.name-only {
            grid-template-columns: 1fr;
        }
        .crd-linked-fields-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            padding-top: 18px;
            border-top: 1px dashed #e2e8f0;
        }
        .crd-linked-field-section {
            padding-top: 18px;
            border-top: 1px dashed #e2e8f0;
        }
        .crd-required-doc-field {
            max-width: 100%;
        }
        .crd-form-group {
            display: flex;
            flex-direction: column;
            gap: 6px;
            text-align: left;
        }
        .crd-form-label {
            font-size: 12px;
            font-weight: 700;
            color: #334155;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 4px;
        }
        .crd-form-help {
            font-size: 11.5px;
            color: #64748b;
            margin-top: 2px;
        }
        .crd-form-control {
            width: 100%;
            height: 38px;
            padding: 8px 12px;
            font-size: 13.5px;
            color: #1e293b;
            background-color: #ffffff;
            border: 1px solid #cbd5e1;
            border-radius: 0 !important;
            transition: all 0.15s ease;
            outline: none;
            box-sizing: border-box;
        }
        .crd-form-control:focus {
            border-color: #2563eb;
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }
        .crd-form-control:disabled {
            background-color: #f1f5f9;
            color: #94a3b8;
            cursor: not-allowed;
        }
        .crd-submit-btn {
            min-height: 36px;
            padding: 7px 22px;
        }
        @media (max-width: 768px) {
            .crd-add-main-grid,
            .crd-linked-fields-grid {
                grid-template-columns: 1fr;
            }
            .crd-required-doc-field {
                max-width: 100%;
            }
        }
      `}</style>

      <Modal.Header>
        <div className="rt-header-shell">
          <div className="rt-header-top">
            <div className="rt-header-main">
              <i className="rt-header-icon fa fa-cogs" aria-hidden="true" />
              <div className="rt-header-copy">
                <h2 className="rt-modal-title">
                  <span className="rt-modal-title-text">
                    <span>Configure & Load Default Documents</span>
                  </span>
                </h2>
                <div className="rt-modal-meta-row">
                  <span className="rt-meta-chip">
                    <i className="fa fa-briefcase" style={{ color: "#2563eb" }} />
                    <span>Performer: {performerType || "Candidate"}</span>
                  </span>
                  <span className="rt-meta-chip">
                    <i className="fa fa-hashtag" style={{ color: "#d97706" }} />
                    <span>Flow: Onboarding Configuration</span>
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="rt-action-btn"
              onClick={handleClose}
              disabled={isProcessing}
              title={isProcessing ? "Please wait until processing finishes" : "Close"}
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="p-0">
        <div className="crd-shell">
          <div className="crd-body">
            <div className="crd-tabs-container">
              <div className="crd-tabs-list">
                <button
                  type="button"
                  className={`crd-tab-item ${activeTab === "documents" ? "active" : ""}`}
                  onClick={() => setActiveTab("documents")}
                >
                  <i className="fa fa-files-o" /> Documents
                  <span className="crd-tab-count">{tempDocuments.length}</span>
                </button>
                {actionTab && !actionTab.bulk && (
                  <button
                    type="button"
                    className={`crd-tab-item ${activeTab === "action" ? "active" : ""}`}
                    onClick={() => setActiveTab("action")}
                  >
                    <i className={
                      actionTab.type === "purchaseOrder" || actionTab.type === "purchaseOrderUpload"
                        ? "fa fa-shopping-cart"
                        : actionTab.type === "generate"
                          ? "fa fa-magic"
                          : "fa fa-paperclip"
                    } />
                    {actionTab.type === "purchaseOrder"
                      ? "Generate Purchase Order"
                      : actionTab.type === "purchaseOrderUpload"
                        ? "Upload Purchase Order"
                        : actionTab.type === "generate"
                          ? "Generate"
                          : "Upload Reference"}: {actionTab.doc.name}
                    <span
                      role="button"
                      tabIndex={0}
                      className="crd-tab-close"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloseActionTab();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") handleCloseActionTab();
                      }}
                    >
                      <i className="fa fa-times" />
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  className={`crd-tab-item ${activeTab === "add" ? "active" : ""}`}
                  disabled={bulkProcess.active}
                  onClick={() => {
                    setEditIndex(null);
                    setNewDocName("");
                    setNewDocAction("upload");
                    setNewDdrmsDocId("");
                    setNewMappingId("");
                    setNewReqDocId("");
                    setActiveTab("add");
                  }}
                >
                  {editIndex !== null ? (
                    <>
                      <i className="fa fa-pencil" /> Edit Document
                    </>
                  ) : (
                    <>
                      <i className="fa fa-plus-circle" /> Add New Document
                    </>
                  )}
                </button>
              </div>
            </div>

            {bulkProcess.status !== "idle" && bulkProcess.total > 0 && (
              <div className={`crd-bulk-progress ${bulkProcess.status}`}>
                <div className="crd-bulk-progress-head">
                  <span>
                    {bulkProcess.status === "running" && (
                      <><i className="fa fa-circle-o-notch fa-spin" /> Processing {bulkProcess.completed + 1} of {bulkProcess.total}: {actionTab?.doc?.name}</>
                    )}
                    {bulkProcess.status === "complete" && (
                      <><i className="fa fa-check-circle" /> All selected documents processed</>
                    )}
                    {bulkProcess.status === "cancelled" && (
                      <><i className="fa fa-exclamation-circle" /> Bulk processing cancelled after {bulkProcess.completed} of {bulkProcess.total}</>
                    )}
                  </span>
                  <span>{bulkProgressPercent}%</span>
                </div>
                <progress className="crd-bulk-progress-native" value={bulkProgressPercent} max="100" aria-label="Bulk document processing progress" />
              </div>
            )}

            {actionTab?.bulk && (
              <div className="crd-background-processor" aria-hidden="true">
                {actionTab.type === "purchaseOrder"
                  ? renderPurchaseOrder && renderPurchaseOrder(actionTab.doc, handleCloseActionTab, getGenerateRenderOptions(actionTab.doc, true))
                  : actionTab.type === "generate"
                    ? renderGenerate && renderGenerate(actionTab.doc, handleCloseActionTab, getGenerateRenderOptions(actionTab.doc, true))
                    : renderUploadReference && renderUploadReference(actionTab.doc, handleCloseActionTab, getUploadRenderOptions(actionTab.doc, true))}
              </div>
            )}

            {activeTab === "action" && actionTab && !actionTab.bulk ? (
              <div className="crd-action-tab-pane">
                <div className="crd-action-body-toolbar">
                  <button type="button" className="crd-btn crd-btn-sm" onClick={() => handleCloseActionTab(false)}>
                    <i className="fa fa-arrow-left" /> Back to Documents
                  </button>
                </div>
                {actionTab.type === "purchaseOrder" || actionTab.type === "purchaseOrderUpload"
                  ? renderPurchaseOrder && renderPurchaseOrder(
                    actionTab.doc,
                    handleCloseActionTab,
                    getGenerateRenderOptions(actionTab.doc, false),
                    actionTab.type === "purchaseOrderUpload" ? "upload" : "generate"
                  )
                  : actionTab.type === "generate"
                    ? renderGenerate && renderGenerate(actionTab.doc, handleCloseActionTab, getGenerateRenderOptions(actionTab.doc, false))
                    : renderUploadReference && renderUploadReference(actionTab.doc, handleCloseActionTab, getUploadRenderOptions(actionTab.doc, false))}
              </div>
            ) : activeTab !== "add" ? (
              <div className="tab-pane-list">
                {/* 1. Generate Documents Section */}
                {activeTab === "documents" && (
                  <div className="crd-section-panel">
                    <div className="crd-section-head">
                      <h3 className="section-heading crd-section-heading-reset">
                        <i className="fa fa-file-pdf-o" /> Generate Documents ({generateDocs.length})
                      </h3>
                      {eligibleGenerateDocs.length > 0 && (
                        <div
                          className={`crd-checkbox-row crd-select-all ${allGenerateSelected ? "selected" : ""} ${isProcessing ? "disabled" : ""}`}
                          onClick={handleToggleAllGenerate}
                        >
                          <span className="crd-checkbox-wrap">
                            <span className="crd-checkbox-custom" />
                          </span>
                          <span>Select All</span>
                        </div>
                      )}
                    </div>

                    {generateDocs.length > 0 ? (
                      <div className="pdm-doc-list">
                        {generateDocs.map(({ doc, idx }) => (
                          <div key={idx} className="pdm-doc-row pdm-doc-row-card">
                            <div className="pdm-doc-check crd-doc-check-center">
                              <div
                                className={`crd-checkbox-row crd-checkbox-trigger ${!isBulkEligible(doc) || isProcessing ? "disabled" : ""} ${isBulkEligible(doc) && selectedDocsToLoad.includes(idx) ? "selected" : ""}`}
                                onClick={() => isBulkEligible(doc) && handleToggleTempDoc(idx)}
                              >
                                <span className="crd-checkbox-wrap">
                                  <span className="crd-checkbox-custom" />
                                </span>
                              </div>
                            </div>
                            <div className="pdm-doc-file">
                              <span className="pdm-doc-file-icon"><i className="fa fa-file-pdf-o" /></span>
                              <div className="pdm-doc-file-main">
                                <div className="pdm-doc-title-row">
                                  <span className="pdm-doc-required-label">{doc.name}</span>
                                </div>
                                <div className="pdm-doc-meta">
                                  {isPurchaseOrderDocument(doc) ? (
                                    <span>
                                      <strong>Purchase Order For:</strong>{" "}
                                      {doc.purchaseOrderType === "recruiter" ? "Recruiter" : "Subcontractor"}
                                    </span>
                                  ) : (
                                    <>
                                      <span><strong>DDRMS Doc ID:</strong> {doc.ddrmsDocId || <span className="crd-not-linked">Not Linked</span>}</span>
                                      <span>•</span>
                                      <span><strong>Mapping ID:</strong> {doc.mappingId || <span className="crd-not-linked">Not Linked</span>}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="pdm-doc-actions-panel crd-doc-actions">
                              {["generated", "uploaded"].includes(bulkItemStatuses[getBulkItemKey(
                                isPurchaseOrderDocument(doc) ? "purchaseOrder" : "generate",
                                doc,
                                idx
                              )]) && (
                                  <span className="crd-row-status">
                                    <i className="fa fa-check-circle" />
                                    {bulkItemStatuses[getBulkItemKey(
                                      isPurchaseOrderDocument(doc) ? "purchaseOrder" : "generate",
                                      doc,
                                      idx
                                    )] === "uploaded" ? "Uploaded" : "Generated"}
                                  </span>
                                )}
                              {!["generated", "uploaded"].includes(bulkItemStatuses[getBulkItemKey(
                                isPurchaseOrderDocument(doc) ? "purchaseOrder" : "generate",
                                doc,
                                idx
                              )]) && stagedGeneratedDocs[getGeneratedKey(doc)] && (
                                  <span className="crd-row-status staged">
                                    <i className={getStagedPurchaseOrder(doc)?.kind === "uploaded" ? "fa fa-cloud-upload" : "fa fa-file-pdf-o"} />
                                    {getStagedPurchaseOrder(doc)?.kind === "uploaded" ? "Uploaded Ready" : "Generated Ready"}
                                  </span>
                                )}
                              {isPurchaseOrderDocument(doc) && renderPurchaseOrder ? (
                                <>
                                  <button
                                    type="button"
                                    className="crd-btn crd-btn-sm crd-action-btn"
                                    onClick={() => handleOpenActionTab("purchaseOrder", doc)}
                                    disabled={getStagedPurchaseOrder(doc)?.kind === "uploaded" || isProcessing}
                                    title="Generate Purchase Order"
                                  >
                                    <i className="fa fa-magic" /> Generate
                                  </button>
                                  <button
                                    type="button"
                                    className="crd-btn crd-btn-sm crd-action-btn"
                                    onClick={() => handleOpenActionTab("purchaseOrderUpload", doc)}
                                    disabled={getStagedPurchaseOrder(doc)?.kind === "generated" || isProcessing}
                                    title="Upload Purchase Order"
                                  >
                                    <i className="fa fa-cloud-upload" /> Upload PO
                                  </button>
                                  {getStagedPurchaseOrder(doc) && (
                                    <button
                                      type="button"
                                      className="crd-btn crd-btn-sm crd-btn-ghost-danger crd-action-btn"
                                      onClick={() => handleRemoveStagedPurchaseOrder(doc, idx)}
                                      disabled={isProcessing}
                                      title="Remove staged Purchase Order"
                                    >
                                      <i className="fa fa-times" /> Remove PO
                                    </button>
                                  )}
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="crd-btn crd-btn-sm crd-action-btn"
                                  onClick={() => handleOpenActionTab("generate", doc)}
                                  disabled={!isGenerateReady(doc) || isProcessing}
                                  title="Generate Document"
                                >
                                  <i className="fa fa-magic" /> Generate
                                </button>
                              )}
                              <button
                                type="button"
                                className="crd-btn crd-btn-sm crd-btn-ghost-indigo crd-icon-action-btn"
                                onClick={() => handleEditDocClick(idx)}
                                disabled={isProcessing}
                                title="Edit Document"
                              >
                                <i className="fa fa-pencil" />
                              </button>
                              <button
                                type="button"
                                className="crd-btn crd-btn-sm crd-btn-ghost-danger crd-icon-action-btn"
                                onClick={() => handleDeleteDoc(idx)}
                                disabled={isProcessing}
                                title="Remove Document"
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="crd-empty-config">
                        <i className="fa fa-file-text-o crd-empty-config-icon" />
                        <span className="crd-empty-config-text">No generated documents configured.</span>
                      </div>
                    )}
                  </div>
                )}

                {/* 2. Upload Documents Section */}
                {activeTab === "documents" && (
                  <div className="crd-section-panel">
                    <div className="crd-section-head">
                      <h3 className="section-heading crd-section-heading-reset">
                        <i className="fa fa-cloud-upload" /> Require Documents ({uploadDocs.length})
                      </h3>
                      {eligibleUploadDocs.length > 0 && (
                        <div
                          className={`crd-checkbox-row crd-select-all ${allUploadSelected ? "selected" : ""} ${isProcessing ? "disabled" : ""}`}
                          onClick={handleToggleAllUpload}
                        >
                          <span className="crd-checkbox-wrap">
                            <span className="crd-checkbox-custom" />
                          </span>
                          <span>Select All</span>
                        </div>
                      )}
                    </div>

                    {uploadDocs.length > 0 ? (
                      <div className="pdm-doc-list">
                        {uploadDocs.map(({ doc, idx }) => (
                          <div key={idx} className="pdm-doc-row pdm-doc-row-card">
                            <div className="pdm-doc-check crd-doc-check-center">
                              <div
                                className={`crd-checkbox-row crd-checkbox-trigger ${!doc.reqDocId || isProcessing ? "disabled" : ""} ${doc.reqDocId && selectedDocsToLoad.includes(idx) ? "selected" : ""}`}
                                onClick={() => doc.reqDocId && handleToggleTempDoc(idx)}
                              >
                                <span className="crd-checkbox-wrap">
                                  <span className="crd-checkbox-custom" />
                                </span>
                              </div>
                            </div>
                            <div className="pdm-doc-file">
                              <span className="pdm-doc-file-icon"><i className="fa fa-list-alt" /></span>
                              <div className="pdm-doc-file-main">
                                <div className="pdm-doc-title-row">
                                  <span className="pdm-doc-required-label">{doc.name}</span>
                                </div>
                                <div className="pdm-doc-meta">
                                  <span><strong>Required Doc ID:</strong> {doc.reqDocId || <span className="crd-not-linked">Not Linked</span>}</span>
                                </div>
                              </div>
                            </div>
                            <div className="pdm-doc-actions-panel crd-doc-actions">
                              {bulkItemStatuses[getBulkItemKey("upload", doc, idx)] === "uploaded" && (
                                <span className="crd-row-status"><i className="fa fa-check-circle" /> Uploaded</span>
                              )}
                              {bulkItemStatuses[getBulkItemKey("upload", doc, idx)] !== "uploaded" && stagedReferenceDocs[getReferenceKey(doc)] && (
                                <span className="crd-row-status staged"><i className="fa fa-paperclip" /> Reference Ready</span>
                              )}
                              <button
                                type="button"
                                className="crd-btn crd-btn-sm crd-action-btn"
                                onClick={() => handleOpenActionTab("upload", doc)}
                                disabled={!doc.reqDocId || isProcessing}
                                title="Upload Reference Document"
                              >
                                <i className="fa fa-paperclip" /> Upload Reference Doc
                              </button>
                              <button
                                type="button"
                                className="crd-btn crd-btn-sm crd-btn-ghost-indigo crd-icon-action-btn"
                                onClick={() => handleEditDocClick(idx)}
                                disabled={isProcessing}
                                title="Edit Document"
                              >
                                <i className="fa fa-pencil" />
                              </button>
                              <button
                                type="button"
                                className="crd-btn crd-btn-sm crd-btn-ghost-danger crd-icon-action-btn"
                                onClick={() => handleDeleteDoc(idx)}
                                disabled={isProcessing}
                                title="Remove Document"
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="crd-empty-config">
                        <i className="fa fa-cloud-upload crd-empty-config-icon" />
                        <span className="crd-empty-config-text">No require documents configured.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="tab-pane-add-doc crd-add-tab">
                <div className="crd-section-panel crd-add-panel">
                  <div className="crd-section-head">
                    <h3 className="section-heading crd-section-heading-reset">
                      <i className={editIndex !== null ? "fa fa-pencil" : "fa fa-plus-circle"} />{" "}
                      {editIndex !== null ? "Edit Onboarding Document" : "Register New Onboarding Document"}
                    </h3>
                  </div>
                  <p className="crd-add-intro">
                    {editingPurchaseOrder
                      ? "Update the Purchase Order display name below."
                      : editIndex !== null
                        ? "Modify the properties of the selected document below."
                        : "Provide the document name and document type below. Once added, it will populate in the checklist."}
                  </p>
                  <form id="default-documents-add-form" onSubmit={handleSaveDoc} className="crd-add-form">
                    <div className={`crd-add-main-grid ${editingPurchaseOrder ? "name-only" : ""}`}>
                      <div className="crd-form-group">
                        <label className="crd-form-label">
                          Document Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="crd-form-control"
                          value={newDocName}
                          onChange={(e) => setNewDocName(e.target.value)}
                          placeholder="e.g. Non-Disclosure Agreement"
                          required
                        />
                        <span className="crd-form-help">The display name shown on candidate's checklist</span>
                      </div>
                      {!editingPurchaseOrder && (
                        <div className="crd-form-group">
                          <label className="crd-form-label">Document Type</label>
                          <select
                            className="crd-form-control"
                            value={newDocAction}
                            onChange={(e) => setNewDocAction(e.target.value)}
                          >
                            <option value="upload">Require Document</option>
                            <option value="generate">Generate</option>
                          </select>
                          <span className="crd-form-help">Action behavior</span>
                        </div>
                      )}
                    </div>

                    {!editingPurchaseOrder && (newDocAction === "generate" ? (
                      <div className="crd-linked-fields-grid">
                        <div className="crd-form-group">
                          <label className="crd-form-label">DDRMS Doc ID</label>
                          <select
                            className="crd-form-control"
                            value={newDdrmsDocId}
                            onChange={(e) => setNewDdrmsDocId(e.target.value)}
                          >
                            <option value="">-- Select DDRMS Template --</option>
                            {ddrmsDocs.filter(doc => doc.fileExtension === "docx").map((doc) => (
                              <option key={doc.id} value={doc.id}>
                                {doc.name} (ID: {doc.id})
                              </option>
                            ))}
                          </select>
                          <span className="crd-form-help">Select template from DDRMS Repository</span>
                        </div>
                        <div className="crd-form-group">
                          <label className="crd-form-label">Mapping ID</label>
                          <select
                            className="crd-form-control"
                            value={newMappingId}
                            onChange={(e) => setNewMappingId(e.target.value)}
                          >
                            <option value="">-- Select Mapping Profile --</option>
                            {dataTypeMappings.map((mapping) => (
                              <option key={mapping.id} value={mapping.id}>
                                {mapping.name}{mapping.type ? ` (${mapping.type})` : ""} (ID: {mapping.id})
                              </option>
                            ))}
                          </select>
                          <span className="crd-form-help">Select field placeholder mapping profile</span>
                        </div>
                      </div>
                    ) : (
                      <div className="crd-linked-field-section">
                        <div className="crd-form-group crd-required-doc-field">
                          <label className="crd-form-label">Required Document ID</label>
                          <select
                            className="crd-form-control"
                            value={newReqDocId}
                            onChange={(e) => setNewReqDocId(e.target.value)}
                          >
                            <option value="">-- Select Required Document --</option>
                            {reqDocuments.map((doc) => (
                              <option key={doc.id} value={doc.id}>
                                {doc.name} (ID: {doc.id})
                              </option>
                            ))}
                          </select>
                          <span className="crd-form-help">Select database required document to link</span>
                        </div>
                      </div>
                    ))}

                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      {activeTab === "documents" && (
      <Modal.Footer className="crd-footer">
        <button
          type="button"
          className="wf-modal-footer-close-btn"
          onClick={handleClose}
          disabled={isProcessing}
        >
          <i className="fa fa-times" aria-hidden="true" /> Cancel
        </button>
        <button
          type="button"
          className="crd-btn primary"
          onClick={handleStartBulkUpload}
          disabled={selectedDocsToLoad.length === 0 || bulkProcess.active}
        >
          {bulkProcess.active ? (
            <><i className="fa fa-circle-o-notch fa-spin" /> Processing {bulkProcess.completed + 1}/{bulkProcess.total}</>
          ) : (
            <><i className="fa fa-cloud-upload" /> Upload All Selected</>
          )}
        </button>
        <button
          type="button"
          className="crd-btn primary"
          onClick={handleSave}
          disabled={isProcessing}
        >
          <i className="fa fa-save" /> Save Config
        </button>
      </Modal.Footer>
      )}
      {activeTab === "action" && actionTab && !actionTab.bulk && (
        <Modal.Footer className="crd-footer">
          <div ref={setActionFooterElement} className="crd-action-footer-host" />
        </Modal.Footer>
      )}
      {activeTab === "add" && (
        <Modal.Footer className="crd-footer">
          <button
            type="button"
            className="wf-modal-footer-close-btn"
            onClick={handleCancelDocumentEdit}
          >
            <i className="fa fa-times" aria-hidden="true" /> Cancel
          </button>
          <button
            type="submit"
            form="default-documents-add-form"
            className="crd-btn primary crd-submit-btn"
          >
            <i className={editIndex !== null ? "fa fa-save" : "fa fa-plus"} />{" "}
            {editIndex !== null ? "Update Document" : "Add Document to Checklist"}
          </button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default DefaultDocumentsModal;
